<template>
  <div class="championship-members">
    <div v-if="!stages.length" class="members-empty">
      {{ $t('championship.members.noStageYet') }}
    </div>

    <div v-else class="members-stage-frame">
      <div class="members-toolbar">
        <div class="stage-selector">
          <span class="active-stage-label">{{ $t('championship.members.viewingStage') }}</span>
          <div class="stage-pills">
            <button
              v-for="stage in stages"
              :key="stage.id"
              type="button"
              class="stage-pill"
              :class="{ 'is-active': selectedStageId === stage.id }"
              :disabled="loadingStage"
              @click="selectStage(stage)"
            >
              {{ stageLabel(stage) }}
            </button>
          </div>
        </div>
        <button class="download-btn" type="button" @click="downloadCsv" :disabled="loadingStage">
          <i class="fa-regular fa-download mr-1" aria-hidden="true"></i>
          {{ $t('championship.members.download') }}
        </button>
      </div>

      <div v-if="loadingStage" class="members-loading">
        <div class="loading-spinner" aria-hidden="true"></div>
        <span class="loading-text">{{ $t('championship.loading') }}</span>
      </div>

      <Table
        v-else-if="stageTournament && stageTournament.participants && stageTournament.participants.length"
        :items="stageTournament.participants"
        :expandable="false"
        :admin-mode="false"
      />

      <div v-else class="members-empty">
        {{ $t('championship.members.empty') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import Table from '@/components/tournament-pos-table/Table.vue';
import TournamentRepository from '@/repositories/tournament';
import { findActiveStage, stageLabel } from '@/services/championship.service';

export default {
  name: 'ChampionshipMembers',
  components: { Table },
  props: {
    overview: { type: Object, default: null },
    participants: { type: Array, default: () => [] },
  },
  data() {
    return {
      stageTournament: null,
      loadingStage: false,
      previousTournament: null,
      // Stage the user is currently viewing. Defaults to the active stage but
      // can be switched to any stage via the pills to inspect past rankings.
      selectedStageId: null,
    };
  },
  computed: {
    // All stages ordered for the selector. Each wraps its own tournament, so
    // every stage — including finished ones — has its own ranking to show.
    stages() {
      return [...(this.overview?.stages || [])]
        .sort((a, b) => (a.stageOrder || 0) - (b.stageOrder || 0));
    },
    activeStage() {
      return findActiveStage(this.overview);
    },
    selectedStage() {
      return this.stages.find((s) => s.id === this.selectedStageId) || this.activeStage || null;
    },
  },
  watch: {
    // Reset the selection to the active stage whenever the championship loads
    // or changes, then load that stage's ranking.
    activeStage: {
      immediate: true,
      handler(stage) {
        if (!stage) return;
        if (this.selectedStageId == null) this.selectedStageId = stage.id;
      },
    },
    selectedStage: {
      immediate: true,
      handler(stage, prev) {
        if (!stage || (prev && prev.id === stage.id)) return;
        this.loadStageTournament(stage);
      },
    },
  },
  beforeDestroy() {
    // Restore whatever tournament was in the store before we mounted, so the
    // championship Members tab doesn't leak stage-tournament data into the
    // rest of the app (e.g. existing tournament views the user navigates back to).
    if (this.previousTournament !== null) {
      this.setTournament(this.previousTournament);
    }
  },
  methods: {
    ...mapMutations(['setTournament']),
    selectStage(stage) {
      if (!stage || this.loadingStage || this.selectedStageId === stage.id) return;
      this.selectedStageId = stage.id;
    },
    stageLabel(stage) {
      if (!stage) return '';
      const key = stage.stageType ? `championship.stage.${stage.stageType}` : '';
      const translated = key ? this.$t(key) : '';
      return translated && translated !== key ? translated : stageLabel(stage);
    },
    async loadStageTournament(stage) {
      if (!stage?.tournamentId) return;
      this.loadingStage = true;
      // Stash the existing tournament so we can restore it on teardown.
      // Only stash on the very first stage load — subsequent stage swaps are still
      // championship-internal and should keep the original snapshot.
      if (this.previousTournament === null) {
        this.previousTournament = this.$store.state.tournament;
      }
      try {
        const res = await TournamentRepository.getTournamentOverview(stage.tournamentId);
        if (res.status < 400) {
          this.stageTournament = res.data;
          // Table.vue and its child components read from $store.state.tournament
          // directly (awards, ranking type, selectedFeatures, etc.). Commit the
          // stage's tournament so they render with correct context.
          this.setTournament(res.data);
        }
      } catch (err) {
        console.error('[championship] loading stage tournament failed', err);
      } finally {
        this.loadingStage = false;
      }
    },
    downloadCsv() {
      const items = this.stageTournament?.participants || [];
      if (!items.length) return;
      const headers = ['rank', 'name', 'username', 'country', 'score', 'status'];
      const rows = items.map((p, idx) => [
        p.place ?? idx + 1,
        `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        p.username || '',
        p.country || '',
        p.score ?? '',
        p.status || '',
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `championship-${this.overview?.id || 'members'}-stage-${this.selectedStage?.stageOrder || ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.championship-members {
  font-family: 'Avenir', sans-serif;
  width: 100%;
}

.members-stage-frame {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.members-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stage-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  flex-wrap: wrap;

  .active-stage-label { color: #6a6a6a; }
}

.stage-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.stage-pill {
  background: transparent;
  border: 1px solid #d0d0d0;
  color: #6a6a6a;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 16px;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled):not(.is-active) {
    border-color: var(--championship-accent, #c03131);
    color: var(--championship-accent, #c03131);
  }

  &.is-active {
    background: var(--championship-accent, #c03131);
    border-color: var(--championship-accent, #c03131);
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.download-btn {
  display: inline-flex;
  align-items: center;
  background: #1b1b1b;
  color: #fff;
  border: none;
  border-radius: 18px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: #333; }
}

.members-empty {
  text-align: center;
  color: #9a9a9a;
  padding: 60px 12px;
  font-size: 14px;
}

.members-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 60px 0;
  color: #6a6a6a;

  .loading-spinner {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid rgba(0, 0, 0, 0.08);
    border-top-color: var(--championship-accent, #c03131);
    animation: members-spin 0.85s linear infinite;
  }
}

@keyframes members-spin {
  to { transform: rotate(360deg); }
}

.theme--dark .championship-members {
  .stage-pill {
    border-color: rgba(255, 255, 255, 0.18);
    color: #ababab;

    &.is-active {
      background: var(--championship-accent, #c03131);
      border-color: var(--championship-accent, #c03131);
      color: #fff;
    }
  }
  .download-btn { background: #f8f8f8; color: #1b1b1b; }
}
</style>
